var VoiceWaveRenderCoreV4 = (function(exports) {
  "use strict";
  class DesignDocumentValidationError extends Error {
    code;
    path;
    issues;
    constructor(issue2) {
      const issues = Array.isArray(issue2) ? issue2 : [issue2];
      super(issues.map((item) => `${item.path}: ${item.message}`).join("; "));
      this.name = "DesignDocumentValidationError";
      this.code = issues[0]?.code || "invalid-design-document";
      this.path = issues[0]?.path || "$";
      this.issues = issues;
    }
  }
  function issue(code, path, message) {
    throw new DesignDocumentValidationError({ code, path, message });
  }
  function canonicalNumber(value) {
    if (!Number.isFinite(value)) throw new Error("Cannot canonicalize a non-finite number");
    const rounded = Math.round(value * 1e6) / 1e6;
    return Object.is(rounded, -0) ? 0 : rounded;
  }
  function normalizeColor(value, path = "color") {
    if (typeof value !== "string") issue("invalid-color", path, "expected an sRGB hex color");
    const source = value.trim().toLowerCase();
    const short = /^#([0-9a-f]{3})$/i.exec(source);
    if (short) return `#${[...short[1]].map((character) => `${character}${character}`).join("")}`;
    if (/^#[0-9a-f]{6}$/i.test(source)) return source;
    issue("invalid-color", path, "expected #rgb or #rrggbb");
  }
  function segmentGraphemes(value) {
    const codePoints = Array.from(value.normalize("NFC"));
    const clusters = [];
    let regionalRun = 0;
    const isMark = (character) => {
      const code = character.codePointAt(0) || 0;
      return code >= 768 && code <= 879 || code >= 6832 && code <= 6911 || code >= 7616 && code <= 7679 || code >= 8400 && code <= 8447 || code >= 65056 && code <= 65071;
    };
    const isVariation = (character) => {
      const code = character.codePointAt(0) || 0;
      return code >= 65024 && code <= 65039 || code >= 917760 && code <= 917999;
    };
    const isRegional = (character) => {
      const code = character.codePointAt(0) || 0;
      return code >= 127462 && code <= 127487;
    };
    for (let index = 0; index < codePoints.length; index += 1) {
      const character = codePoints[index];
      const previous = codePoints[index - 1];
      const joinPrevious = isMark(character) || isVariation(character) || character === "‍" || previous === "‍" || clusters.at(-1)?.endsWith("‍");
      if (isRegional(character)) regionalRun += 1;
      else regionalRun = 0;
      if (!clusters.length || !joinPrevious && !(isRegional(character) && regionalRun % 2 === 0)) clusters.push(character);
      else clusters[clusters.length - 1] += character;
    }
    return clusters;
  }
  function countGraphemes(value) {
    return segmentGraphemes(value).length;
  }
  function stableValue(value) {
    if (typeof value === "number") return canonicalNumber(value);
    if (Array.isArray(value)) return value.map(stableValue);
    if (value && typeof value === "object") {
      return Object.fromEntries(Object.keys(value).sort().map((key) => [key, stableValue(value[key])]));
    }
    return value;
  }
  function stableSerialize(value) {
    return JSON.stringify(stableValue(value));
  }
  const SHA256_K = [
    1116352408,
    1899447441,
    3049323471,
    3921009573,
    961987163,
    1508970993,
    2453635748,
    2870763221,
    3624381080,
    310598401,
    607225278,
    1426881987,
    1925078388,
    2162078206,
    2614888103,
    3248222580,
    3835390401,
    4022224774,
    264347078,
    604807628,
    770255983,
    1249150122,
    1555081692,
    1996064986,
    2554220882,
    2821834349,
    2952996808,
    3210313671,
    3336571891,
    3584528711,
    113926993,
    338241895,
    666307205,
    773529912,
    1294757372,
    1396182291,
    1695183700,
    1986661051,
    2177026350,
    2456956037,
    2730485921,
    2820302411,
    3259730800,
    3345764771,
    3516065817,
    3600352804,
    4094571909,
    275423344,
    430227734,
    506948616,
    659060556,
    883997877,
    958139571,
    1322822218,
    1537002063,
    1747873779,
    1955562222,
    2024104815,
    2227730452,
    2361852424,
    2428436474,
    2756734187,
    3204031479,
    3329325298
  ];
  function rotateRight(value, amount) {
    return value >>> amount | value << 32 - amount;
  }
  function sha256Hex(value) {
    const bytes = new TextEncoder().encode(value);
    const bitLength = bytes.length * 8;
    const paddedLength = bytes.length + 9 + 63 >> 6 << 6;
    const data = new Uint8Array(paddedLength);
    data.set(bytes);
    data[bytes.length] = 128;
    const view = new DataView(data.buffer);
    view.setUint32(paddedLength - 4, bitLength >>> 0);
    view.setUint32(paddedLength - 8, Math.floor(bitLength / 4294967296));
    let h0 = 1779033703;
    let h1 = 3144134277;
    let h2 = 1013904242;
    let h3 = 2773480762;
    let h4 = 1359893119;
    let h5 = 2600822924;
    let h6 = 528734635;
    let h7 = 1541459225;
    const schedule = new Uint32Array(64);
    for (let offset = 0; offset < paddedLength; offset += 64) {
      for (let index = 0; index < 16; index += 1) schedule[index] = view.getUint32(offset + index * 4);
      for (let index = 16; index < 64; index += 1) {
        const valueA = schedule[index - 15];
        const valueB = schedule[index - 2];
        const smallA = rotateRight(valueA, 7) ^ rotateRight(valueA, 18) ^ valueA >>> 3;
        const smallB = rotateRight(valueB, 17) ^ rotateRight(valueB, 19) ^ valueB >>> 10;
        schedule[index] = schedule[index - 16] + smallA + schedule[index - 7] + smallB >>> 0;
      }
      let a = h0;
      let b = h1;
      let c = h2;
      let d = h3;
      let e = h4;
      let f = h5;
      let g = h6;
      let h = h7;
      for (let index = 0; index < 64; index += 1) {
        const bigA = rotateRight(a, 2) ^ rotateRight(a, 13) ^ rotateRight(a, 22);
        const choose = e & f ^ ~e & g;
        const temp1 = h + (rotateRight(e, 6) ^ rotateRight(e, 11) ^ rotateRight(e, 25)) + choose + SHA256_K[index] + schedule[index] >>> 0;
        const majority = a & b ^ a & c ^ b & c;
        const temp2 = bigA + majority >>> 0;
        h = g;
        g = f;
        f = e;
        e = d + temp1 >>> 0;
        d = c;
        c = b;
        b = a;
        a = temp1 + temp2 >>> 0;
      }
      h0 = h0 + a >>> 0;
      h1 = h1 + b >>> 0;
      h2 = h2 + c >>> 0;
      h3 = h3 + d >>> 0;
      h4 = h4 + e >>> 0;
      h5 = h5 + f >>> 0;
      h6 = h6 + g >>> 0;
      h7 = h7 + h >>> 0;
    }
    return [h0, h1, h2, h3, h4, h5, h6, h7].map((word) => word.toString(16).padStart(8, "0")).join("");
  }
  const DESIGN_DOCUMENT_SCHEMA_VERSION_V4 = "voicewave-design-v4";
  const RENDERER_VERSION_V4 = "waveform-renderer-v4";
  const V4_LAYOUTS = ["classic", "circle", "heart"];
  const V4_PAINT_MODES = ["solid", "gradient"];
  class V4DesignValidationError extends Error {
    issues;
    constructor(issue2) {
      const issues = Array.isArray(issue2) ? issue2 : [issue2];
      super(issues.map((item) => `${item.path}: ${item.message}`).join("; "));
      this.name = "V4DesignValidationError";
      this.issues = issues;
    }
  }
  const V4_FONT_CATALOG = Object.freeze({
    "elegant-script-v1": { family: "VoiceWave Allura", sha256: "9c142b2e515832c0dfc4ff8b8ea18f40314943bf937b72e2b23c4661bac14cc6", file: "Allura-Regular.ttf", license: "SIL Open Font License 1.1" },
    "parisienne-v1": { family: "VoiceWave Parisienne", sha256: "bc9ee17f022e20bc700797e5f557d14bfa43af0c98d9e6c9c5c1ca4ec7aacd57", file: "Parisienne-Regular.ttf", license: "SIL Open Font License 1.1" },
    "great-vibes-v1": { family: "VoiceWave Great Vibes", sha256: "8d509802186f1b51572531ecf313e8098f9a5bfdfaca93f0c9b34467f9982d15", file: "GreatVibes-Regular.ttf", license: "SIL Open Font License 1.1" },
    "clean-sans-v1": { family: "VoiceWave Lato", sha256: "d636e4683231f931eda222d588e944d082bfd3bdba02f928bee461c0f185b251", file: "Lato-Regular.ttf", license: "SIL Open Font License 1.1" },
    "classic-serif-v1": { family: "VoiceWave Abril Fatface", sha256: "5971d4a3758a922a9fedc7f6fb825a96341a2e718c45a4b2c9a6b417c8c4dbe9", file: "AbrilFatface-Regular.ttf", license: "SIL Open Font License 1.1" }
  });
  const V4_FONT_OPTIONS = Object.freeze([
    { fontId: "great-vibes-v1", label: "Great Vibes", preview: "A voice to keep" },
    { fontId: "elegant-script-v1", label: "Allura", preview: "A voice to keep" },
    { fontId: "parisienne-v1", label: "Parisienne", preview: "A voice to keep" },
    { fontId: "clean-sans-v1", label: "Lato", preview: "A voice to keep" }
  ]);
  const V4_PALETTE_PRESETS = Object.freeze({
    "rose-gold": { mode: "gradient", stops: [{ offset: 0, color: "#d99a86" }, { offset: 0.5, color: "#b56b72" }, { offset: 1, color: "#7a4053" }], background: "#ffffff", text: "#2b2020" },
    "warm-vow": { mode: "gradient", stops: [{ offset: 0, color: "#c7441f" }, { offset: 0.5, color: "#9c174b" }, { offset: 1, color: "#4f1230" }], background: "#ffffff", text: "#171717" },
    "midnight-gold": { mode: "gradient", stops: [{ offset: 0, color: "#e7c77d" }, { offset: 0.5, color: "#b88a3d" }, { offset: 1, color: "#7a4b1e" }], background: "#101010", text: "#f5ead3" },
    "ocean-teal": { mode: "gradient", stops: [{ offset: 0, color: "#62b6cb" }, { offset: 0.5, color: "#1b4965" }, { offset: 1, color: "#0b2545" }], background: "#ffffff", text: "#0b2545" },
    "emerald-forest": { mode: "gradient", stops: [{ offset: 0, color: "#87a96b" }, { offset: 0.5, color: "#2d5a27" }, { offset: 1, color: "#132a13" }], background: "#ffffff", text: "#132a13" },
    "gallery-black": { mode: "solid", stops: [{ offset: 0, color: "#000000" }], background: "#ffffff", text: "#171717" },
    "clay-memory": { mode: "gradient", stops: [{ offset: 0, color: "#d7a28f" }, { offset: 0.5, color: "#be7a67" }, { offset: 1, color: "#8d4e43" }], background: "#fbf8f4", text: "#3b2a26" },
    "navy-gold": { mode: "gradient", stops: [{ offset: 0, color: "#d8b46d" }, { offset: 0.5, color: "#456785" }, { offset: 1, color: "#203651" }], background: "#ffffff", text: "#1e2d3f" },
    // Legacy aliases remain readable for older operator tools and saved previews.
    "near-black": { mode: "solid", stops: [{ offset: 0, color: "#171717" }], background: "#ffffff", text: "#171717" },
    "warm-sunset": { mode: "gradient", stops: [{ offset: 0, color: "#c7441f" }, { offset: 0.5, color: "#9c174b" }, { offset: 1, color: "#4f1230" }], background: "#ffffff", text: "#171717" },
    "rose-wine": { mode: "gradient", stops: [{ offset: 0, color: "#b83b5e" }, { offset: 0.5, color: "#7d234f" }, { offset: 1, color: "#30152d" }], background: "#ffffff", text: "#171717" }
  });
  const USER_PRESET = Object.freeze({ barCount: 840, strokeWidth: 3.4, innerScale: 2, outerScale: 2, gamma: 0.81, minimumLength: 1, opacity: 0.59, fillOpacity: 0.01 });
  const CLASSIC_USER_SELECTED_PRESET = Object.freeze({ barCount: 1380, strokeRatio: 0.63, amplitude: 1.06, gamma: 0.56, minimumLength: 1.2, smoothing: 1, opacity: 0.98, color: "#000000", background: "#ffffff" });
  const FORBIDDEN_V4_KEYS = /* @__PURE__ */ new Set(["barCount", "strokeWidth", "innerScale", "outerScale", "gamma", "minimumLength", "opacity", "fillOpacity", "waveform", "canonicalPeaks", "canonicalMinMax", "minMax", "peaksData", "svg", "svgMarkup", "script", "html"]);
  function fail(code, path, message) {
    throw new V4DesignValidationError({ code, path, message });
  }
  function objectValue(value, path) {
    if (!value || typeof value !== "object" || Array.isArray(value)) fail("invalid-object", path, "expected an object");
    return value;
  }
  function stringValue(value, path, required = true, max = 400) {
    if (value == null || value === "") {
      if (!required) return "";
      fail("required-string", path, "expected a non-empty string");
    }
    if (typeof value !== "string") fail("invalid-string", path, "expected a string");
    const normalized = value.normalize("NFC");
    if (required && !normalized.trim()) fail("required-string", path, "expected a non-empty string");
    if ([...normalized].length > max) fail("text-too-long", path, `must contain at most ${max} Unicode code points`);
    return normalized;
  }
  function finiteNumber(value, path, fallback, min, max) {
    const result = value == null || value === "" ? fallback : Number(value);
    if (!Number.isFinite(result) || result < min || result > max) fail("invalid-number", path, `must be a finite number between ${min} and ${max}`);
    return canonicalNumber(result);
  }
  function luminance(hex) {
    const channels = [0, 2, 4].map((offset) => Number.parseInt(hex.slice(1 + offset, 3 + offset), 16) / 255).map((value) => value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4);
    return channels[0] * 0.2126 + channels[1] * 0.7152 + channels[2] * 0.0722;
  }
  function contrastRatio(first, second) {
    const a = luminance(first);
    const b = luminance(second);
    return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
  }
  function normalizeStops(paint, background) {
    const mode = String(paint.mode || "gradient").toLowerCase();
    if (!V4_PAINT_MODES.includes(mode)) fail("unsupported-paint-mode", "paint.mode", "must be solid or gradient");
    const raw = Array.isArray(paint.stops) ? paint.stops : [];
    if (mode === "solid") {
      const color = normalizeColor(paint.color ?? raw[0]?.color ?? "#171717", "paint.color");
      if (contrastRatio(color, background) < 1.6) fail("low-contrast-paint", "paint.color", "wave color must remain visible against the background");
      return { mode, stops: [{ offset: 0, color }] };
    }
    if (raw.length < 2 || raw.length > 4) fail("gradient-stop-count", "paint.stops", "gradient requires 2-4 color stops");
    const stops = raw.map((entry, index) => {
      const stop = objectValue(entry, `paint.stops[${index}]`);
      return { offset: finiteNumber(stop.offset, `paint.stops[${index}].offset`, index / Math.max(1, raw.length - 1), 0, 1), color: normalizeColor(stop.color, `paint.stops[${index}].color`) };
    }).sort((a, b) => a.offset - b.offset);
    if (stops[0].offset !== 0 || stops.at(-1)?.offset !== 1) fail("gradient-offset-range", "paint.stops", "gradient stops must start at 0 and end at 1");
    for (let index = 1; index < stops.length; index += 1) if (stops[index - 1].offset >= stops[index].offset) fail("gradient-offset-order", "paint.stops", "gradient offsets must be strictly increasing");
    if (Math.min(...stops.map((stop) => contrastRatio(stop.color, background))) < 1.6) fail("low-contrast-paint", "paint.stops", "every wave color must remain visible against the background");
    return { mode, stops };
  }
  function rejectForbidden(value, path = "$") {
    if (!value || typeof value !== "object") return;
    if (Array.isArray(value)) return value.forEach((item, index) => rejectForbidden(item, `${path}[${index}]`));
    for (const [key, child] of Object.entries(value)) {
      if (FORBIDDEN_V4_KEYS.has(key)) fail("preset-parameters-forbidden", `${path}.${key}`, "renderer parameters are server-owned and must be selected by presetId");
      rejectForbidden(child, `${path}.${key}`);
    }
  }
  function canonicalizeDesignDocumentV4(input) {
    rejectForbidden(input);
    const source = objectValue(input, "$");
    if (String(source.schemaVersion || DESIGN_DOCUMENT_SCHEMA_VERSION_V4) !== DESIGN_DOCUMENT_SCHEMA_VERSION_V4) fail("unsupported-schema", "schemaVersion", `must be ${DESIGN_DOCUMENT_SCHEMA_VERSION_V4}`);
    if (String(source.rendererVersion || RENDERER_VERSION_V4) !== RENDERER_VERSION_V4) fail("unsupported-renderer", "rendererVersion", `must be ${RENDERER_VERSION_V4}`);
    const layout = String(source.layout || "").toLowerCase();
    if (!V4_LAYOUTS.includes(layout)) fail("unsupported-layout", "layout", "must be classic, circle, or heart");
    const expectedPreset = layout === "classic" ? "classic-gallery-wave-v3" : "user-selected-v1";
    const presetId = String(source.presetId || expectedPreset);
    if (presetId !== expectedPreset) fail("preset-layout-mismatch", "presetId", `layout ${layout} requires ${expectedPreset}`);
    const trim = objectValue(source.trim, "trim");
    const startMs = finiteNumber(trim.startMs, "trim.startMs", 0, 0, 864e5);
    const endMs = finiteNumber(trim.endMs, "trim.endMs", startMs, 0, 864e5);
    if (endMs < startMs) fail("invalid-trim", "trim", "endMs must be greater than or equal to startMs");
    const peaks = objectValue(source.peaks, "peaks");
    const revision = stringValue(peaks.revision, "peaks.revision", true, 120);
    const peaksSha = stringValue(peaks.sha256, "peaks.sha256", true, 80).toLowerCase();
    if (!/^[0-9a-f]{8,64}$/u.test(peaksSha)) fail("invalid-peaks-hash", "peaks.sha256", "must be an 8-64 character hexadecimal hash");
    const background = normalizeColor(source.background ?? "#ffffff", "background");
    const paint = normalizeStops(objectValue(source.paint, "paint"), background);
    const textSource = objectValue(source.text, "text");
    const fontId = stringValue(textSource.fontId ?? (layout === "classic" ? "classic-serif-v1" : "great-vibes-v1"), "text.fontId", true, 80);
    if (!(fontId in V4_FONT_CATALOG)) fail("unsupported-font", "text.fontId", "font must be a bundled, hash-pinned font");
    const textColor = normalizeColor(textSource.color ?? "#171717", "text.color");
    if (contrastRatio(textColor, background) < 3) fail("low-contrast-text", "text.color", "text color must have at least 3:1 contrast against the background");
    const text = { title: stringValue(textSource.title, "text.title", false, 160), kicker: stringValue(textSource.kicker, "text.kicker", false, 96), date: stringValue(textSource.date, "text.date", false, 80), fontId, color: textColor };
    return { schemaVersion: DESIGN_DOCUMENT_SCHEMA_VERSION_V4, rendererVersion: RENDERER_VERSION_V4, memoryId: stringValue(source.memoryId, "memoryId", true, 120), productUid: stringValue(source.productUid, "productUid", true, 240), layout, presetId, trim: { startMs, endMs }, peaks: { revision, sha256: peaksSha }, paint: { mode: paint.mode, mapping: "along-time", stops: paint.stops }, background, text };
  }
  function validateDesignDocumentV4(input) {
    try {
      return { ok: true, value: canonicalizeDesignDocumentV4(input) };
    } catch (error) {
      if (error instanceof V4DesignValidationError) return { ok: false, errors: error.issues };
      throw error;
    }
  }
  function canonicalPeaksHashV4(value) {
    return sha256Hex(stableSerialize(normalizeSignedMinMaxV4(value)));
  }
  function normalizeSignedMinMaxV4(value) {
    if (!Array.isArray(value) || value.length < 24) fail("invalid-minmax", "peaks", "at least 24 signed min/max pairs are required");
    return value.map((entry, index) => {
      const object = Array.isArray(entry) ? { min: entry[0], max: entry[1] } : objectValue(entry, `peaks[${index}]`);
      const min = Number(object.min);
      const max = Number(object.max);
      if (!Number.isFinite(min) || !Number.isFinite(max)) fail("invalid-minmax", `peaks[${index}]`, "min/max must be finite");
      return min <= max ? { min: canonicalNumber(min), max: canonicalNumber(max) } : { min: canonicalNumber(max), max: canonicalNumber(min) };
    });
  }
  function fontFactor(fontId) {
    return ["great-vibes-v1", "elegant-script-v1", "parisienne-v1"].includes(fontId) ? 0.34 : fontId === "classic-serif-v1" ? 0.54 : 0.56;
  }
  function textWidth(value, size, fontId) {
    return countGraphemes(value) * size * fontFactor(fontId);
  }
  function wrapByWidth(value, size, fontId, maxWidth, maxLines) {
    const words = value.trim().split(/\s+/u).filter(Boolean);
    const lines = [];
    let current = "";
    for (const word of words) {
      const candidate = current ? `${current} ${word}` : word;
      if (textWidth(candidate, size, fontId) <= maxWidth) current = candidate;
      else {
        if (current) lines.push(current);
        if (textWidth(word, size, fontId) <= maxWidth) current = word;
        else {
          const clusters = segmentGraphemes(word);
          for (let offset = 0; offset < clusters.length; offset += Math.max(1, Math.floor(maxWidth / Math.max(1, size * fontFactor(fontId))))) lines.push(clusters.slice(offset, offset + Math.max(1, Math.floor(maxWidth / Math.max(1, size * fontFactor(fontId))))).join(""));
          current = "";
        }
      }
    }
    if (current) lines.push(current);
    return lines.length <= maxLines ? lines : null;
  }
  function measureTextLayoutV4(document, geometry) {
    const fontId = document.text.fontId;
    const font = V4_FONT_CATALOG[fontId];
    const safe = geometry.safe;
    const centerX = safe.x + safe.width / 2;
    const centerY = safe.y + safe.height * (document.layout === "classic" ? 0.72 : document.layout === "heart" ? 0.51 : 0.5);
    const maxWidth = safe.width * (document.layout === "classic" ? 0.74 : document.layout === "heart" ? 0.46 : 0.54);
    const maxHeight = safe.height * (document.layout === "classic" ? 0.3 : document.layout === "heart" ? 0.25 : 0.34);
    const classicAspect = safe.height / Math.max(1, safe.width);
    const classicBaseScale = classicAspect < 0.42 ? 0.05 : 0.058;
    const base = document.layout === "classic" ? Math.max(28, safe.width * classicBaseScale) : Math.max(28, Math.min(safe.width, safe.height) * 0.076);
    const min = document.layout === "classic" ? 22 : 18;
    const minimumDatePxAt300Dpi = 34;
    const fields = [["kicker", base * 0.34, min * 0.74, 1], ["title", base, min, 2], ["date", Math.max(base * 0.32, minimumDatePxAt300Dpi), Math.max(min * 0.68, minimumDatePxAt300Dpi), 1]];
    const blocks = {};
    let ratio = 1;
    for (const [field, requestedSize, minimumSize, maxLines] of fields) {
      const value = document.text[field];
      if (!value) continue;
      const blockFontId = field === "title" ? fontId : "clean-sans-v1";
      let size = requestedSize;
      let lines = [value];
      while (size > minimumSize && textWidth(lines[0], size, blockFontId) > maxWidth) size *= 0.94;
      if (textWidth(lines[0], size, blockFontId) > maxWidth) {
        lines = wrapByWidth(value, minimumSize, blockFontId, maxWidth, maxLines) || [];
        size = minimumSize;
      }
      if (!lines.length || lines.some((line) => textWidth(line, size, blockFontId) > maxWidth + 0.01)) return { status: "blocked", fontId, fontFamily: font.family, fontHash: font.sha256, blocks, bounds: null, textVisualScale: { ratio: 0, minFontSize: minimumSize }, error: `${field} is too wide for the ${document.layout} safe area; shorten it or choose a larger product.` };
      ratio = Math.min(ratio, size / requestedSize);
      const lineHeight = size * 1.16;
      const width = Math.max(...lines.map((line) => textWidth(line, size, blockFontId)));
      blocks[field] = { field, lines, fontSize: canonicalNumber(size), lineHeight: canonicalNumber(lineHeight), baseline: 0, width: canonicalNumber(width), fontId: blockFontId };
    }
    const ordered = fields.map(([field]) => blocks[field]).filter(Boolean);
    const gap = base * 0.22;
    const totalHeight = ordered.reduce((sum, block) => sum + block.lineHeight * block.lines.length, 0) + gap * Math.max(0, ordered.length - 1);
    if (totalHeight > maxHeight) return { status: "blocked", fontId, fontFamily: font.family, fontHash: font.sha256, blocks, bounds: null, textVisualScale: { ratio: 0, minFontSize: min }, error: `text group is too tall for the ${document.layout} safe area; shorten the supporting text or main message.` };
    let cursor = centerY - totalHeight / 2;
    for (const block of ordered) {
      block.baseline = canonicalNumber(cursor + block.fontSize * 0.86);
      cursor += block.lineHeight * block.lines.length + gap;
    }
    const rects = Object.values(blocks).flatMap((block) => block.lines.map((_, index) => ({ x: centerX - block.width / 2, y: block.baseline + index * block.lineHeight - block.fontSize * 0.86, width: block.width, height: block.fontSize })));
    const bounds = rects.length ? { x: Math.min(...rects.map((rect) => rect.x)), y: Math.min(...rects.map((rect) => rect.y)), width: Math.max(...rects.map((rect) => rect.x + rect.width)) - Math.min(...rects.map((rect) => rect.x)), height: Math.max(...rects.map((rect) => rect.y + rect.height)) - Math.min(...rects.map((rect) => rect.y)) } : null;
    return { status: "fit", fontId, fontFamily: font.family, fontHash: font.sha256, blocks, bounds, textVisualScale: { ratio: canonicalNumber(ratio), minFontSize: min } };
  }
  function distance(a, b) {
    return Math.hypot(a.x - b.x, a.y - b.y);
  }
  function cubic(p0, p1, p2, p3, t) {
    const u = 1 - t;
    return { x: u ** 3 * p0.x + 3 * u ** 2 * t * p1.x + 3 * u * t ** 2 * p2.x + t ** 3 * p3.x, y: u ** 3 * p0.y + 3 * u ** 2 * t * p1.y + 3 * u * t ** 2 * p2.y + t ** 3 * p3.y };
  }
  function heartSegments() {
    const p = (x, y) => ({ x, y });
    return [[p(500, 620), p(450, 570), p(250, 430), p(220, 330)], [p(220, 330), p(190, 230), p(290, 180), p(370, 220)], [p(370, 220), p(430, 250), p(470, 300), p(500, 330)], [p(500, 330), p(530, 300), p(570, 250), p(630, 220)], [p(630, 220), p(710, 180), p(810, 230), p(780, 330)], [p(780, 330), p(750, 430), p(550, 570), p(500, 620)]];
  }
  function sourcePathPoints(layout, sampleCount = 4096) {
    if (layout === "circle") return Array.from({ length: sampleCount }, (_, index) => {
      const angle = -Math.PI / 2 + Math.PI * 2 * index / sampleCount;
      return { x: 500 + Math.cos(angle) * 250, y: 380 + Math.sin(angle) * 250 };
    });
    if (layout === "classic") return Array.from({ length: sampleCount }, (_, index) => ({ x: index / Math.max(1, sampleCount - 1) * 1e3, y: 380 }));
    const dense = [];
    const segments = heartSegments();
    const per = Math.max(32, Math.ceil(sampleCount / segments.length));
    for (const [p0, p1, p2, p3] of segments) for (let index = 0; index < per; index += 1) dense.push(cubic(p0, p1, p2, p3, index / per));
    const lengths = [0];
    for (let index = 1; index < dense.length; index += 1) lengths[index] = lengths[index - 1] + distance(dense[index - 1], dense[index]);
    const total = lengths.at(-1) || 1;
    const points = [];
    for (let index = 0; index < sampleCount; index += 1) {
      const target = total * index / sampleCount;
      let right = lengths.findIndex((length) => length >= target);
      if (right < 1) right = 1;
      const left = right - 1;
      const span = Math.max(1e-9, lengths[right] - lengths[left]);
      const ratio = (target - lengths[left]) / span;
      points.push({ x: dense[left].x + (dense[right].x - dense[left].x) * ratio, y: dense[left].y + (dense[right].y - dense[left].y) * ratio });
    }
    return points;
  }
  function sampleGeometry(layout, count) {
    const points = sourcePathPoints(layout, Math.max(4096, count * 5));
    const sampled = Array.from({ length: count }, (_, index) => points[Math.floor(index * points.length / count)]);
    let previousNormal = null;
    return sampled.map((point, index) => {
      const previous = sampled[(index - 1 + sampled.length) % sampled.length];
      const next = sampled[(index + 1) % sampled.length];
      let tx = next.x - previous.x;
      let ty = next.y - previous.y;
      const length = Math.hypot(tx, ty) || 1;
      tx /= length;
      ty /= length;
      let nx = -ty;
      let ny = tx;
      const radialX = point.x - 500;
      const radialY = point.y - 380;
      if (nx * radialX + ny * radialY < 0) {
        nx *= -1;
        ny *= -1;
      }
      if (previousNormal && nx * previousNormal.x + ny * previousNormal.y < 0) {
        nx *= -1;
        ny *= -1;
      }
      previousNormal = { x: nx, y: ny };
      return { ...point, normalX: nx, normalY: ny };
    });
  }
  function resamplePairs(value, target) {
    const source = normalizeSignedMinMaxV4(value);
    const pairs = [];
    for (let column = 0; column < target; column += 1) {
      const from = Math.floor(column * source.length / target);
      const to = Math.max(from + 1, Math.ceil((column + 1) * source.length / target));
      let min = 0;
      let max = 0;
      for (let index = from; index < to && index < source.length; index += 1) {
        min = Math.min(min, source[index].min);
        max = Math.max(max, source[index].max);
      }
      pairs.push({ min: canonicalNumber(min), max: canonicalNumber(max) });
    }
    return pairs;
  }
  function resampleClassicPairs(value, target) {
    const source = normalizeSignedMinMaxV4(value);
    const pairs = [];
    for (let column = 0; column < target; column += 1) {
      const from = Math.floor(column * source.length / target);
      const to = Math.max(from + 1, Math.floor((column + 1) * source.length / target));
      let min = 1;
      let max = -1;
      for (let index = from; index < to && index < source.length; index += 1) {
        min = Math.min(min, source[index].min);
        max = Math.max(max, source[index].max);
      }
      pairs.push({ min: canonicalNumber(min === 1 ? 0 : min), max: canonicalNumber(max === -1 ? 0 : max) });
    }
    return pairs;
  }
  function smoothPairs(value, radius) {
    if (radius <= 0) return value;
    return value.map((_, index) => {
      let min = 0;
      let max = 0;
      let weightTotal = 0;
      for (let offset = Math.max(0, index - radius); offset <= Math.min(value.length - 1, index + radius); offset += 1) {
        const weight = radius + 1 - Math.abs(offset - index);
        min += value[offset].min * weight;
        max += value[offset].max * weight;
        weightTotal += weight;
      }
      return { min: canonicalNumber(min / weightTotal), max: canonicalNumber(max / weightTotal) };
    });
  }
  function sourceToCanvas(point, geometry) {
    const scale = Math.min(geometry.safe.width / 1e3, geometry.safe.height / 760);
    return { x: geometry.safe.x + (geometry.safe.width - 1e3 * scale) / 2 + point.x * scale, y: geometry.safe.y + (geometry.safe.height - 760 * scale) / 2 + point.y * scale, scale };
  }
  function colorAt(stops, position) {
    if (stops.length === 1) return stops[0].color;
    const t = Math.max(0, Math.min(1, position));
    const right = stops.findIndex((stop) => stop.offset >= t);
    const index = right < 0 ? stops.length - 1 : Math.max(1, right);
    const leftStop = stops[index - 1];
    const rightStop = stops[index] || leftStop;
    const ratio = (t - leftStop.offset) / Math.max(1e-9, rightStop.offset - leftStop.offset);
    const rgb = (hex) => [0, 2, 4].map((offset) => Number.parseInt(hex.slice(1 + offset, 3 + offset), 16));
    const leftRgb = rgb(leftStop.color);
    const rightRgb = rgb(rightStop.color);
    return `#${leftRgb.map((channel, channelIndex) => Math.round(channel + (rightRgb[channelIndex] - channel) * ratio).toString(16).padStart(2, "0")).join("")}`;
  }
  function pathNumber(value) {
    return canonicalNumber(value).toFixed(3);
  }
  function escapeXml(value) {
    return value.replace(/[&<>"']/g, (character) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&apos;" })[character] || character);
  }
  function localFill(layout, outer, inner, colors, scale, geometry) {
    if (layout === "circle") {
      const path = `M ${outer.map((point) => `${pathNumber(point.x)} ${pathNumber(point.y)}`).join(" L ")} L ${inner.slice().reverse().map((point) => `${pathNumber(point.x)} ${pathNumber(point.y)}`).join(" L ")} Z`;
      return { markup: `<path d="${path}" fill="${colors[0]}" fill-opacity="0.01" data-fill-role="diagnostic-circle-ribbon"/>`, segmentCount: 1, skippedCuspSegments: 0, maxTangentialEdge: 0, maxRadialEdge: 0 };
    }
    const paths = [];
    const cuspGuard = 34 * scale;
    let skipped = 0;
    let maxTangential = 0;
    let maxRadial = 0;
    const cusps = [{ x: 500, y: 330 }, { x: 500, y: 620 }].map((point) => sourceToCanvas(point, geometry));
    for (let index = 2; index < outer.length - 3; index += 1) {
      const next = index + 1;
      const points = [outer[index], outer[next], inner[next], inner[index]];
      if (cusps.some((cusp) => distance(points[0], cusp) < cuspGuard || distance(points[1], cusp) < cuspGuard)) {
        skipped += 1;
        continue;
      }
      const tangentialA = distance(points[0], points[1]);
      const tangentialB = distance(points[2], points[3]);
      const radialA = distance(points[1], points[2]);
      const radialB = distance(points[3], points[0]);
      maxTangential = Math.max(maxTangential, tangentialA, tangentialB);
      maxRadial = Math.max(maxRadial, radialA, radialB);
      const color = colors[Math.min(colors.length - 1, Math.floor(index / Math.max(1, outer.length - 1) * colors.length))] || colors[0];
      paths.push(`<path d="M ${points.map((point) => `${pathNumber(point.x)} ${pathNumber(point.y)}`).join(" L ")} Z" fill="${color}"/>`);
    }
    return { markup: `<g fill-opacity="0.01" data-fill-role="diagnostic-heart-local-quads" data-fill-segment-count="${paths.length}" data-fill-seam="heart-cusp">${paths.join("")}</g>`, segmentCount: paths.length, skippedCuspSegments: skipped, maxTangentialEdge: canonicalNumber(maxTangential), maxRadialEdge: canonicalNumber(maxRadial) };
  }
  function renderWaveformDocumentV4(options) {
    const document = canonicalizeDesignDocumentV4(options.document);
    const geometry = options.geometry;
    const width = Math.round(Number(geometry.width));
    const height = Math.round(Number(geometry.height));
    if (!Number.isFinite(width) || !Number.isFinite(height) || width < 120 || height < 90) fail("invalid-geometry", "geometry", "canvas dimensions are invalid");
    if (document.layout === "classic") {
      const preset2 = CLASSIC_USER_SELECTED_PRESET;
      const pairs2 = smoothPairs(resampleClassicPairs(options.signedMinMax, preset2.barCount), preset2.smoothing);
      const left = width * (55 / 1200);
      const right = width * (1145 / 1200);
      const centerY = height * (380 / 900);
      const range = height * (215 / 900);
      const spacing = (right - left) / Math.max(1, pairs2.length - 1);
      const strokeWidth = spacing * preset2.strokeRatio;
      const display = (value) => Math.max(preset2.minimumLength * height / 900, Math.pow(Math.min(1, Math.abs(value)), preset2.gamma) * range * preset2.amplitude);
      const endpoints = pairs2.map((pair, index) => ({ x: left + index * spacing, innerY: centerY + display(pair.min), outerY: centerY - display(pair.max) }));
      const colors2 = pairs2.map((_, index) => document.paint.mode === "solid" ? document.paint.stops[0].color : colorAt(document.paint.stops, index / Math.max(1, pairs2.length - 1)));
      const lineMarkup2 = endpoints.map((point, index) => `<line x1="${pathNumber(point.x)}" y1="${pathNumber(point.innerY)}" x2="${pathNumber(point.x)}" y2="${pathNumber(point.outerY)}" stroke="${colors2[index]}" stroke-width="${pathNumber(strokeWidth)}" stroke-linecap="butt" opacity="${preset2.opacity}"/>`).join("");
      const font2 = V4_FONT_CATALOG[document.text.fontId];
      const textLayout2 = measureTextLayoutV4(document, geometry);
      if (textLayout2.status !== "fit") fail("text-does-not-fit", "text", textLayout2.error || "text does not fit");
      const textMarkup2 = Object.values(textLayout2.blocks).flatMap((block) => {
        const blockFont = V4_FONT_CATALOG[block.fontId];
        return block.lines.map((line, index) => `<text x="${pathNumber(geometry.safe.x + geometry.safe.width / 2)}" y="${pathNumber(block.baseline + index * block.lineHeight)}" font-size="${pathNumber(block.fontSize)}" font-family="${escapeXml(blockFont.family)}" data-font-id="${block.fontId}" data-font-sha256="${blockFont.sha256}" fill="${document.text.color}" text-anchor="middle">${escapeXml(line)}</text>`);
      }).join("");
      const sourceHash = options.sourceSequenceHash || canonicalPeaksHashV4(options.signedMinMax);
      const geometryPayload2 = { layout: document.layout, preset: preset2, lineEndpoints: endpoints, sourceSequenceHash: sourceHash };
      const geometryFingerprint2 = sha256Hex(stableSerialize(geometryPayload2));
      const styleFingerprint2 = sha256Hex(stableSerialize({ paint: document.paint, background: document.background, text: document.text, fontHash: font2.sha256, presetId: document.presetId }));
      const svg2 = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" data-renderer="${RENDERER_VERSION_V4}" data-layout="classic" data-preset-id="${document.presetId}"><metadata>${escapeXml(JSON.stringify({ schemaVersion: document.schemaVersion, rendererVersion: document.rendererVersion, productUid: document.productUid, peaks: document.peaks, presetId: document.presetId, classicPreset: preset2, geometryFingerprint: geometryFingerprint2, styleFingerprint: styleFingerprint2, fontId: document.text.fontId, fontHash: font2.sha256, textLayout: textLayout2 }))}</metadata><rect width="100%" height="100%" fill="${document.background}"/><g fill="none">${lineMarkup2}</g><g fill="${document.text.color}">${textMarkup2}</g></svg>`;
      const metrics2 = { rendererVersion: RENDERER_VERSION_V4, layout: "classic", lineCount: pairs2.length, strokeWidth: canonicalNumber(strokeWidth), fillOpacity: 0, geometryFingerprint: geometryFingerprint2, styleFingerprint: styleFingerprint2, fontId: document.text.fontId, fontHash: font2.sha256, textLayout: textLayout2, textWaveOverlap: false, clipping: endpoints.some((point) => point.innerY > height || point.outerY < 0), fill: { segmentCount: 0, skippedCuspSegments: 0, maxTangentialEdge: 0, maxRadialEdge: 0 }, sourceSequenceHash: sourceHash };
      return { svg: svg2, metrics: metrics2, geometryFingerprint: geometryFingerprint2, styleFingerprint: styleFingerprint2, document };
    }
    const preset = USER_PRESET;
    const pairs = resamplePairs(options.signedMinMax, preset.barCount);
    const model = sampleGeometry(document.layout, preset.barCount);
    const mapped = [];
    const outer = [];
    const inner = [];
    const scale = Math.min(geometry.safe.width / 1e3, geometry.safe.height / 760);
    const span = document.layout === "circle" ? 76 : 39;
    model.forEach((point, index) => {
      const pair = pairs[index];
      const inward = preset.minimumLength + Math.pow(Math.min(1, Math.abs(pair.min)), preset.gamma) * span * preset.innerScale;
      const outward = preset.minimumLength + Math.pow(Math.min(1, Math.abs(pair.max)), preset.gamma) * span * preset.outerScale;
      const sourceInner = { x: point.x - point.normalX * inward, y: point.y - point.normalY * inward };
      const sourceOuter = { x: point.x + point.normalX * outward, y: point.y + point.normalY * outward };
      const mappedPoint = sourceToCanvas(point, geometry);
      const mappedInner = sourceToCanvas(sourceInner, geometry);
      const mappedOuter = sourceToCanvas(sourceOuter, geometry);
      mapped.push({ ...mappedPoint, normalX: point.normalX, normalY: point.normalY });
      outer.push(mappedOuter);
      inner.push(mappedInner);
    });
    const font = V4_FONT_CATALOG[document.text.fontId];
    const textLayout = measureTextLayoutV4(document, geometry);
    if (textLayout.status !== "fit") fail("text-does-not-fit", "text", textLayout.error || "text does not fit");
    const interiorBounds = { x: geometry.safe.x + geometry.safe.width * 0.24, y: geometry.safe.y + geometry.safe.height * 0.24, width: geometry.safe.width * 0.52, height: geometry.safe.height * 0.4 };
    const textWaveOverlap = Boolean(textLayout.bounds && (textLayout.bounds.x < interiorBounds.x || textLayout.bounds.y < interiorBounds.y || textLayout.bounds.x + textLayout.bounds.width > interiorBounds.x + interiorBounds.width || textLayout.bounds.y + textLayout.bounds.height > interiorBounds.y + interiorBounds.height));
    if (textWaveOverlap) fail("text-wave-overlap", "text", "text exceeds the shape's measured center safe area");
    const colors = pairs.map((_, index) => document.paint.mode === "solid" ? document.paint.stops[0].color : colorAt(document.paint.stops, index / Math.max(1, pairs.length - 1)));
    const lineMarkup = pairs.map((_, index) => `<line x1="${pathNumber(inner[index].x)}" y1="${pathNumber(inner[index].y)}" x2="${pathNumber(outer[index].x)}" y2="${pathNumber(outer[index].y)}" stroke="${colors[index]}" stroke-width="${pathNumber(preset.strokeWidth * scale)}" stroke-linecap="butt" opacity="${preset.opacity}"/>`).join("");
    const fill = localFill(document.layout, outer, inner, colors, scale, geometry);
    const textMarkup = Object.values(textLayout.blocks).flatMap((block) => {
      const blockFont = V4_FONT_CATALOG[block.fontId];
      return block.lines.map((line, index) => `<text x="${pathNumber(geometry.safe.x + geometry.safe.width / 2)}" y="${pathNumber(block.baseline + index * block.lineHeight)}" font-size="${pathNumber(block.fontSize)}" font-family="${escapeXml(blockFont.family)}" data-font-id="${block.fontId}" data-font-sha256="${blockFont.sha256}" fill="${document.text.color}" text-anchor="middle">${escapeXml(line)}</text>`);
    }).join("");
    const gradientDefs = document.paint.mode === "gradient" ? `<defs><linearGradient id="v4-time-gradient" x1="0" y1="0" x2="1" y2="0">${document.paint.stops.map((stop) => `<stop offset="${stop.offset}" stop-color="${stop.color}"/>`).join("")}</linearGradient></defs>` : "";
    const sourceBounds = [...outer, ...inner].reduce((bounds, point) => ({ minX: Math.min(bounds.minX, point.x), minXMax: Math.max(bounds.minXMax, point.x), minY: Math.min(bounds.minY, point.y), maxY: Math.max(bounds.maxY, point.y) }), { minX: Infinity, minXMax: -Infinity, minY: Infinity, maxY: -Infinity });
    const geometryPayload = { layout: document.layout, lineEndpoints: pairs.map((_, index) => [outer[index], inner[index]]), sourceSequenceHash: options.sourceSequenceHash || canonicalPeaksHashV4(options.signedMinMax) };
    const geometryFingerprint = sha256Hex(stableSerialize(geometryPayload));
    const styleFingerprint = sha256Hex(stableSerialize({ paint: document.paint, background: document.background, text: document.text, fontHash: font.sha256, presetId: document.presetId }));
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" data-renderer="${RENDERER_VERSION_V4}" data-layout="${document.layout}" data-preset-id="${document.presetId}"><metadata>${escapeXml(JSON.stringify({ schemaVersion: document.schemaVersion, rendererVersion: document.rendererVersion, productUid: document.productUid, peaks: document.peaks, presetId: document.presetId, geometryFingerprint, styleFingerprint, fontId: document.text.fontId, fontHash: font.sha256, textLayout, sourceBounds }))}</metadata><rect width="100%" height="100%" fill="${document.background}"/>${gradientDefs}${fill.markup}<g fill="none">${lineMarkup}</g><g fill="${document.text.color}">${textMarkup}</g></svg>`;
    const metrics = { rendererVersion: RENDERER_VERSION_V4, layout: document.layout, lineCount: pairs.length, strokeWidth: canonicalNumber(preset.strokeWidth * scale), fillOpacity: preset.fillOpacity, geometryFingerprint, styleFingerprint, fontId: document.text.fontId, fontHash: font.sha256, textLayout, textWaveOverlap: false, clipping: outer.concat(inner).some((point) => point.x < 0 || point.y < 0 || point.x > width || point.y > height), fill: { segmentCount: fill.segmentCount, skippedCuspSegments: fill.skippedCuspSegments, maxTangentialEdge: fill.maxTangentialEdge, maxRadialEdge: fill.maxRadialEdge }, sourceSequenceHash: options.sourceSequenceHash || canonicalPeaksHashV4(options.signedMinMax) };
    return { svg, metrics, geometryFingerprint, styleFingerprint, document };
  }
  const V4_PRESETS = Object.freeze({ "classic-gallery-wave-v3": CLASSIC_USER_SELECTED_PRESET, "user-selected-v1": USER_PRESET });
  exports.CLASSIC_USER_SELECTED_PRESET = CLASSIC_USER_SELECTED_PRESET;
  exports.DESIGN_DOCUMENT_SCHEMA_VERSION_V4 = DESIGN_DOCUMENT_SCHEMA_VERSION_V4;
  exports.RENDERER_VERSION_V4 = RENDERER_VERSION_V4;
  exports.V4DesignValidationError = V4DesignValidationError;
  exports.V4_FONT_CATALOG = V4_FONT_CATALOG;
  exports.V4_FONT_OPTIONS = V4_FONT_OPTIONS;
  exports.V4_LAYOUTS = V4_LAYOUTS;
  exports.V4_PAINT_MODES = V4_PAINT_MODES;
  exports.V4_PALETTE_PRESETS = V4_PALETTE_PRESETS;
  exports.V4_PRESETS = V4_PRESETS;
  exports.canonicalPeaksHashV4 = canonicalPeaksHashV4;
  exports.canonicalizeDesignDocumentV4 = canonicalizeDesignDocumentV4;
  exports.measureTextLayoutV4 = measureTextLayoutV4;
  exports.normalizeSignedMinMaxV4 = normalizeSignedMinMaxV4;
  exports.renderWaveformDocumentV4 = renderWaveformDocumentV4;
  exports.validateDesignDocumentV4 = validateDesignDocumentV4;
  Object.defineProperty(exports, Symbol.toStringTag, { value: "Module" });
  return exports;
})({});
